import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Rating,
  Box,
  Collapse,
  IconButton,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { addReview, getAllTrackScoreDetails, getLearnerNotes } from '../../../../services/mentorServices/ReviewServices';
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const LearnerReviewDetails = () => {
  const { state } = useLocation();
  const { topic } = state || {};

  const [reviewSection, setReviewSection] = useState([]);
  const [ratings, setRatings] = useState({});
  const [expandedSections, setExpandedSections] = useState({});
  const [remark, setRemark] = useState('');
  const [errors, setErrors] = useState({});
  const [ratingSettings, setRatingSettings] = useState([]);
  const [trackScores, setTrackScores] = useState({ tech: 0, learn: 0, comm: 0, total: 0 });
  const [notes, setNotes] = useState([]);
  const navigate = useNavigate();

  const isPublished = topic?.isPublished === 1;

  useEffect(() => {
    if (topic) {
      getAllTrackScoreDetails(
        topic.learner_id,
        topic.topicID,
        topic.moduleID,
        topic.makerplanID
      )
        .then((response) => {
          const details = response.data.data.Scores?.DetailedScore || [];
          const initialRatings = {};
          const initialExpandState = {};

          details.forEach((section) => {
            section.track_parameters.forEach((param) => {
              const key = param.track_parameter_code;
              initialRatings[key] = param.rating_star;
            });
            initialExpandState[section.track_area_code] = false;
          });

          setRatings(initialRatings);
          setReviewSection(details);
          setExpandedSections(initialExpandState);
          setRemark(response.data.data.Scores?.TotalScore?.review_comment || '');
          setRatingSettings(response.data.data.Rating_Setting || []);

          const totalData = response.data.data.Scores?.TotalScore;
          if (totalData) {
            setTrackScores({
              tech: totalData.track_techability_score,
              learn: totalData.track_learnability_score,
              comm: totalData.track_communicability_score,
              total: totalData.track_score,
            });
          }
        })
        .catch((error) => {
          toast.error(error?.message || error.response?.message || error.response?.data?.message || 'Error fetching review details.');
        });

      getLearnerNotes(
        topic.learner_id,
        topic.makerplanID,
        topic.moduleID,
        topic.topicID
      )
        .then((response) => {
          if (response.data.data) {
            setNotes(response.data.data);
          } else {
            setNotes([]);
          }
        })
        .catch((error) => {
          toast.error(error?.message || error.response?.message || 'Error fetching notes.');
        });
    }
  }, [topic]);

  const getPercentageByStar = (star) => {
    const found = ratingSettings.find((r) => r.rating_stars === star);
    return found ? parseFloat(found.rating_percentage) : 0;
  };

  const calculateScores = (updatedRatings) => {
    let techScore = 0, learnScore = 0, commScore = 0;

    reviewSection.forEach((section) => {
      const areaCode = section.track_area_code;
      section.track_parameters.forEach((param) => {
        const star = updatedRatings[param.track_parameter_code] || 0;
        const percentage = getPercentageByStar(star);
        const score = param.track_max_score * percentage;

        if (areaCode === 'T') techScore += score;
        else if (areaCode === 'L') learnScore += score;
        else if (areaCode === 'C') commScore += score;
      });
    });

    setTrackScores({
      tech: techScore / 100,
      learn: learnScore / 100,
      comm: commScore / 100,
      total: (techScore + learnScore + commScore) / 100,
    });
  };

  const handleRatingChange = (event, newValue, paramCode) => {
    if (isPublished) return;
    const updatedRatings = { ...ratings, [paramCode]: newValue };
    setRatings(updatedRatings);
    setErrors((prev) => ({ ...prev, [paramCode]: false }));
    calculateScores(updatedRatings);
  };

  const handleRemarkChange = (event) => {
    if (isPublished) return;
    setRemark(event.target.value);
    setErrors((prev) => ({ ...prev, remark: false }));
  };

  const validateForm = () => {
    const newErrors = {};
    reviewSection.forEach((section) => {
      section.track_parameters.forEach((param) => {
        if (!ratings[param.track_parameter_code]) {
          newErrors[param.track_parameter_code] = true;
        }
      });
    });
    if (!remark.trim()) newErrors.remark = true;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isPublished) return;

    if (!validateForm()) {
      toast.error('Please fill all ratings and overall remark.');
      return;
    }

    try {
      const DetailedScore = reviewSection.map((section) => {
        const updatedParams = section.track_parameters.map((param) => {
          const star = ratings[param.track_parameter_code] || 0;
          const percentage = getPercentageByStar(star);
          const score = (param.track_max_score * percentage) / 100;

          return {
            track_parameter_code: param.track_parameter_code,
            track_parameter_score: score,
            track_max_score: param.track_max_score,
            rating_star: star,
          };
        });

        return {
          track_area_code: section.track_area_code,
          track_parameters: updatedParams,
        };
      });

      const TotalScore = {
        track_score: trackScores.total,
        track_techability_score: trackScores.tech,
        track_learnability_score: trackScores.learn,
        track_communicability_score: trackScores.comm,
        review_comment: remark,
        review_date: new Date().toISOString().split('T')[0],
      };

      const requestPayload = {
        learnerId: topic.learner_id,
        TopicId: topic.topicID,
        ModuleId: topic.moduleID,
        MakerPlanId: topic.makerplanID,
        Scores: {
          DetailedScore,
          TotalScore,
        },
      };

      const response = await addReview(requestPayload);
      const message = response?.data?.message || 'Review submitted successfully.';
      toast.success(message);
      navigate(localStorage.getItem('learnerPath'));
    } catch (error) {
      const errMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Submission failed. Please try again later.';
      toast.error(errMessage);
    }
  };

  const renderSection = (section) => {
    const title = section.track_area;
    let areaScore = 0;
    if (section.track_area_code === 'T') areaScore = trackScores.tech;
    else if (section.track_area_code === 'L') areaScore = trackScores.learn;
    else if (section.track_area_code === 'C') areaScore = trackScores.comm;

    return (
      <Card key={title} sx={{ boxShadow: 3, borderRadius: 2, backgroundColor: '#fff', mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: "0.5rem 1rem" }}>
          <Typography color="#000" sx={{ fontSize: { xs: '1rem', sm: '1rem' }, width: "100%", display: "flex", justifyContent: "space-between" }}>
            <strong>{title}</strong> {areaScore.toFixed(2)}
          </Typography>
          <IconButton onClick={() => setExpandedSections((prev) => ({ ...prev, [section.track_area_code]: !prev[section.track_area_code] }))}>
            {expandedSections[section.track_area_code] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Collapse in={expandedSections[section.track_area_code]} timeout="auto" unmountOnExit>
          <CardContent sx={{ p: "0px 16px !important" }}>
            {section.track_parameters.map((param, index) => (
              <Box key={index} sx={{ mb: 3, display: "flex", flexDirection: 'column', alignItems: 'start' }}>
                <Typography sx={{ fontSize: { xs: '1rem', sm: '1rem' } }}>{param.track_parameter}</Typography>
                <Rating
                  name={param.track_parameter_code}
                  value={ratings[param.track_parameter_code] || 0}
                  max={5}
                  readOnly={isPublished}
                  onChange={(event, newValue) =>
                    handleRatingChange(event, newValue, param.track_parameter_code)
                  }
                  sx={{ fontSize: { xs: '2rem', sm: '2rem' }, color: '#FFD700' }}
                />
                {errors[param.track_parameter_code] && (
                  <Typography variant="caption" color="error" ml={1}>
                    {param.track_parameter_code} rating is required*
                  </Typography>
                )}
              </Box>
            ))}
          </CardContent>
        </Collapse>
      </Card>
    );
  };

  return (
    <Box sx={{ mx: 'auto', p: 0 }}>
      {/* <IconButton
        size="large"
        edge="start"
        color="inherit"
        sx={{ mr: 2 }}
        onClick={() => navigate(-1)}
      >
        <ArrowBackIcon sx={{ fontSize: 30 }} />
      </IconButton> */}
      <Box
        sx={{
          position: 'relative',
          mb: 2,
          px: 2,
          py: 1,
          textAlign: 'center',
          color: 'primary.main',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {topic ? topic.topic_Name : 'Topic Name'}
        </Typography>
        <Typography
          sx={{
            position: 'absolute',
            right: 56,
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: { xs: '0.9rem', sm: '1rem' },
            fontWeight: 'bold',
          }}
        >
          {trackScores.total.toFixed(2)}
        </Typography>
      </Box>

      {reviewSection.map((section) => renderSection(section))}

      <Card sx={{ boxShadow: 3, borderRadius: 2, backgroundColor: '#fff' }}>
        <CardContent>
          <Typography sx={{ fontWeight: 'bold', mb: 1 }}>
            Overall Remark
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={15}
            variant="outlined"
            value={remark}
            onChange={handleRemarkChange}
            error={!!errors.remark}
            helperText={errors.remark ? 'Remark is required*' : ''}
            placeholder="Enter your overall remark here..."
            InputProps={{
              readOnly: isPublished,
            }}
          />
        </CardContent>

        {notes.length > 0 && (
          <>
            <Typography sx={{ fontWeight: 'bold', m: 0, ml: 2 }}>
              Notes :
            </Typography>
            <List sx={{ paddingLeft: 2 }}>
              {notes.map((note, index) => (
                <ListItem key={index} sx={{ padding: 0 }}>
                  <ListItemText
                    primary={`- ${note.track_note}`}
                    sx={{
                      fontSize: '0.9rem',
                      color: 'text.primary',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Tooltip
            title={topic?.isPublished === 1 ? 'Review already published' : ''}
            disableHoverListener={topic?.isPublished !== 1}
          >
            <span>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={topic?.isPublished === 1}
              >
                Submit
              </Button>
            </span>
          </Tooltip>
        </Box>

      </Box>
    </Box>
  );
};

export default LearnerReviewDetails;
